import {
  ATC_DEFAULT_LIMIT,
  ATC_MAX_CONFIGS,
  ATC_MAX_LIMIT,
  type AtcConfig,
  type AtcConfigSummary,
  type AtcDraft,
  type AtcDraftErrors,
  type AtcPayloadConfig,
} from '../models/watchlist.types'

export const ADVERTISER_ID_PATTERN = /^AR\d+$/iu
export const REGION_PATTERN = /^[A-Z]{2}$/u

interface AtcDraftInput {
  config_name?: unknown
  advertiser_ids?: unknown
  advertiser_name?: unknown
  regions?: unknown
  limit?: unknown
  include_removed?: unknown
  advertiserIdInput?: unknown
}

export interface AtcValidationResult {
  valid: boolean
  payload: AtcPayloadConfig[]
  errors: Record<string, AtcDraftErrors>
  capExceeded: boolean
}

export function normalizeTextArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      if (item && typeof item === 'object') {
        const record = item as Record<string, unknown>
        return normalizeTextArray(record.value ?? record.code ?? item)
      }
      return normalizeTextArray(item)
    })
  }
  if (value === null || value === undefined) return []
  return String(value)
    .split(/[,\s]+/u)
    .map((item) => item.trim())
    .filter(Boolean)
}

function uniqueFolded(values: string[]): string[] {
  const seen = new Set<string>()
  return values.filter((value) => {
    const folded = value.toUpperCase()
    if (seen.has(folded)) return false
    seen.add(folded)
    return true
  })
}

export function normalizeAdvertiserIds(value: unknown): string[] {
  return uniqueFolded(normalizeTextArray(value).map((id) => id.toUpperCase()))
}

export function normalizeRegions(value: unknown): string[] {
  return uniqueFolded(normalizeTextArray(value).map((region) => region.toUpperCase()))
}

export function normalizeDraft(input: AtcDraftInput): AtcConfig {
  const advertiserIdInput = input.advertiserIdInput ?? input.advertiser_ids
  const rawLimit =
    input.limit === '' || input.limit === null || input.limit === undefined
      ? ATC_DEFAULT_LIMIT
      : input.limit
  const advertiserName = String(input.advertiser_name ?? '').trim()
  return {
    config_name: String(input.config_name ?? '').trim(),
    advertiser_ids: normalizeAdvertiserIds(advertiserIdInput),
    ...(advertiserName ? { advertiser_name: advertiserName } : {}),
    regions: normalizeRegions(input.regions),
    limit: Number(rawLimit),
    include_removed: Boolean(input.include_removed),
  }
}

export function toPayloadConfig(config: AtcConfig): AtcPayloadConfig {
  return {
    config_name: config.config_name,
    ...(config.advertiser_ids.length ? { advertiser_ids: config.advertiser_ids } : {}),
    ...(config.advertiser_name ? { advertiser_name: config.advertiser_name } : {}),
    ...(config.regions.length ? { regions: config.regions } : {}),
    limit: config.limit,
    include_removed: config.include_removed,
  }
}

export function normalizeSavedConfigs(
  value: AtcPayloadConfig[] | AtcPayloadConfig | null | undefined,
): AtcConfig[] {
  const configs = Array.isArray(value) ? value : value ? [value] : []
  return configs.map((config) => normalizeDraft(config))
}

export function createAtcDraft(uid: string, config?: AtcDraftInput): AtcDraft {
  const normalized = normalizeDraft(config ?? {})
  return {
    ...normalized,
    uid,
    advertiserIdInput: normalized.advertiser_ids[0] ?? '',
    showFilters: false,
    touched: {},
  }
}

export function summarizeConfig(config: AtcDraftInput): AtcConfigSummary {
  const normalized = normalizeDraft(config)
  return {
    advertiserCount: normalized.advertiser_ids.length,
    advertiserName: normalized.advertiser_name ?? '',
    regions: normalized.regions,
    limit: normalized.limit,
  }
}

export function validateDrafts(drafts: readonly AtcDraft[]): AtcValidationResult {
  const errors: Record<string, AtcDraftErrors> = {}
  const payload: AtcPayloadConfig[] = []
  const seenIds = new Set<string>()

  for (const draft of drafts) {
    const fieldErrors: AtcDraftErrors = {}
    const normalized = normalizeDraft(draft)
    const advertiserId = normalized.advertiser_ids[0] ?? ''

    if (!normalized.config_name) fieldErrors.config_name = 'requiredName'
    if (!advertiserId) fieldErrors.advertiser_id = 'requiredAdvertiserId'
    else if (!ADVERTISER_ID_PATTERN.test(advertiserId)) {
      fieldErrors.advertiser_id = 'advertiserIdFormat'
    } else if (seenIds.has(advertiserId.toUpperCase())) {
      fieldErrors.advertiser_id = 'duplicateAdvertiserId'
    }
    if (advertiserId) seenIds.add(advertiserId.toUpperCase())

    if (normalized.regions.some((region) => !REGION_PATTERN.test(region))) {
      fieldErrors.regions = 'regionsFormat'
    }
    if (
      !Number.isInteger(normalized.limit) ||
      normalized.limit < 1 ||
      normalized.limit > ATC_MAX_LIMIT
    ) {
      fieldErrors.limit = 'limitRange'
    }

    if (Object.keys(fieldErrors).length > 0) errors[draft.uid] = fieldErrors
    else payload.push(toPayloadConfig(normalized))
  }

  const capExceeded = drafts.length > ATC_MAX_CONFIGS
  return {
    valid: Object.keys(errors).length === 0 && !capExceeded,
    payload,
    errors,
    capExceeded,
  }
}
